import {
  type BoPage,
  BO_PAGE_SECTIONS,
  BO_PAGES,
  type BoPageSection,
} from "./bo-pages";
import { type DestinationContext, resolveDestination } from "./destinations";
import {
  type CatalogShortcut,
  SHORTCUT_CATALOG,
  SHORTCUT_GROUPS,
  type ShortcutGroup,
} from "./shortcut-catalog";
import { isCustomShortcut, type StoredShortcut } from "./shortcuts";
import { normalizeForSearch } from "./tutorial-search";

/**
 * Contenu de l'écran « Ajouter un raccourci » : ce qui est proposé pour cette
 * instance, filtré par la recherche. Ce qui est déjà ajouté reste affiché
 * (coché) plutôt que de disparaître.
 */

type VisibleT = (key: string, fallback: string) => string;

export type PickerAction = {
  entry: CatalogShortcut;
  label: string;
  used: boolean;
};

export type PickerActionGroup = {
  id: ShortcutGroup;
  label: string;
  actions: PickerAction[];
};

export type PickerPage = { page: BoPage; label: string; used: boolean };

export type PickerPageGroup = {
  id: BoPageSection;
  label: string;
  pages: PickerPage[];
};

function matches(label: string, query: string): boolean {
  const q = normalizeForSearch(query.trim());
  return !q || normalizeForSearch(label).includes(q);
}

export function pickerActionGroups(
  stored: StoredShortcut[],
  ctx: DestinationContext,
  query: string,
  vt: VisibleT,
): PickerActionGroup[] {
  const used = new Set(stored.map((s) => s.id));
  const actions = SHORTCUT_CATALOG.flatMap((entry): PickerAction[] => {
    if (resolveDestination(entry.destination, ctx) === null) return [];
    const label = vt(entry.labelKey, entry.label);
    return matches(label, query)
      ? [{ entry, label, used: used.has(entry.id) }]
      : [];
  });
  return SHORTCUT_GROUPS.flatMap((group): PickerActionGroup[] => {
    const inGroup = actions.filter((a) => a.entry.group === group.id);
    return inGroup.length
      ? [
          {
            id: group.id,
            label: vt(group.labelKey, group.label),
            actions: inGroup,
          },
        ]
      : [];
  });
}

export function pickerPageGroups(
  stored: StoredShortcut[],
  ctx: DestinationContext,
  query: string,
  vt: VisibleT,
): PickerPageGroup[] {
  const usedPaths = new Set(
    stored.filter(isCustomShortcut).map((s) => s.custom.path),
  );
  const pages = BO_PAGES.flatMap((page): PickerPage[] => {
    if (page.available && !page.available(ctx)) return [];
    const label = vt(page.labelKey, page.label);
    return matches(label, query)
      ? [{ page, label, used: usedPaths.has(page.path) }]
      : [];
  });
  return BO_PAGE_SECTIONS.flatMap((section): PickerPageGroup[] => {
    const inSection = pages.filter((p) => p.page.section === section.id);
    return inSection.length
      ? [
          {
            id: section.id,
            label: vt(section.labelKey, section.label),
            pages: inSection,
          },
        ]
      : [];
  });
}
